/**
 * Turnstile for "begin a story" (the guard requires a token once TURNSTILE_SITE_KEY is set). Invisible: the script loads
 * only when a key is configured, on the first new story, and its token rides in the cf-turnstile-response header.
 */
declare global {
  interface Window {
    turnstile?: { render(el: HTMLElement, o: Record<string, unknown>): string; remove(id: string): void };
  }
}

let loading: Promise<void> | null = null;
const load = () => (loading ??= new Promise<void>((resolve, reject) => {
  const s = document.createElement("script");
  s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  s.async = true;
  s.onload = () => resolve();
  s.onerror = () => { loading = null; reject(new Error("turnstile script")); };
  document.head.append(s);
}));

/** How long the check may take on its own, and how long a player has once it asks them for a tap. */
const WAIT_MS = 20_000, TAP_MS = 120_000;

/**
 * A fresh token, or undefined when no key is configured (or the check could not run: the server then says so).
 * The widget is Managed (dashboard): usually it passes on its own and never shows; when it wants a tap it appears
 * above the writing line ("interaction-only"). It used to be rendered with size "invisible", which a Managed widget
 * doesn't accept, so no token ever came and every new story was refused (2026-10-18).
 */
export async function turnstileToken(siteKey: string | null, action = "begin"): Promise<string | undefined> {
  if (!siteKey) return undefined;
  try { await load(); } catch { return undefined; }
  const el = document.createElement("div");
  el.className = "lm-turnstile";
  document.body.append(el);
  return new Promise((resolve) => {
    let id: string | undefined;
    let settled = false;
    const done = (token?: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(token);
      try { if (id !== undefined) window.turnstile!.remove(id); } catch { /* already gone */ }
      el.remove();
    };
    let timer = window.setTimeout(() => done(undefined), WAIT_MS);
    try {
      id = window.turnstile!.render(el, {
        sitekey: siteKey, action, appearance: "interaction-only",
        callback: (token: string) => done(token),
        "before-interactive-callback": () => { window.clearTimeout(timer); timer = window.setTimeout(() => done(undefined), TAP_MS); },
        "error-callback": (code: string) => { console.warn(`turnstile error ${code}`); done(undefined); },
        "timeout-callback": () => done(undefined),
        "expired-callback": () => done(undefined),
      });
    } catch { done(undefined); }
  });
}
